'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import {
  Gavel, Clock, TrendingUp, Search, Filter, RefreshCw,
  Star, MapPin, Calendar, Fuel, Gauge, Loader2,
  ChevronDown, X, Sparkles, AlertCircle,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/context/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { auctionsApi, type Auction, type AuctionFilters } from '@/lib/api/auctions';
import { useCountry } from '@/context/CountryContext';
import { getCurrencyLabel } from '@/lib/currency';

const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

function toAbsUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('http')) return raw;
  return `${API}${raw.startsWith('/') ? '' : '/'}${raw}`;
}

// ── Countdown Hook ────────────────────────────────────────────────────────────

function useCountdown(endDatetime: string) {
  const [secs, setSecs] = useState(0);

  useEffect(() => {
    const calc = () => {
      const diff = Math.max(0, Math.floor((new Date(endDatetime).getTime() - Date.now()) / 1000));
      setSecs(diff);
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [endDatetime]);

  if (secs <= 0) return { label: '--:--:--', urgent: false };
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const label = h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return { label, urgent: secs < 3600 };
}

// ── Status Badge ──────────────────────────────────────────────────────────────

function AuctionStatusBadge({ status, lang }: { status: string; lang: string }) {
  const config: Record<string, { label: string; labelAr: string; cls: string }> = {
    live:             { label: 'Live',         labelAr: 'نشط الآن',     cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    ended:            { label: 'Ended',        labelAr: 'منتهي',        cls: 'bg-slate-100 text-slate-600 border-slate-200' },
    sold:             { label: 'Sold',         labelAr: 'مُباع',        cls: 'bg-blue-100 text-blue-800 border-blue-200' },
    pending_approval: { label: 'Pending',      labelAr: 'قيد المراجعة', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    approved:         { label: 'Upcoming',     labelAr: 'قريباً',       cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
    cancelled:        { label: 'Cancelled',    labelAr: 'ملغي',         cls: 'bg-red-100 text-red-800 border-red-200' },
    draft:            { label: 'Draft',        labelAr: 'مسودة',        cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  };
  const c = config[status] || config.draft;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${c.cls}`}>
      {lang === 'ar' ? c.labelAr : c.label}
    </span>
  );
}

// ── Auction Card ──────────────────────────────────────────────────────────────

function AuctionCard({ auction, lang, isRTL, currencyLabel }: { auction: Auction; lang: string; isRTL: boolean; currencyLabel: string }) {
  const countdown = useCountdown(auction.end_datetime);
  const imgUrl = toAbsUrl(auction.primary_image_url);
  const endingSoon = auction.status === 'live' && countdown.urgent;

  return (
    <Link href={`/auctions/${auction.id}`}>
      <Card className="group h-full flex flex-col overflow-hidden border-none shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all bg-white rounded-3xl cursor-pointer">
        {/* Image */}
        <div className="relative h-44 bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden rounded-t-3xl">
          {imgUrl ? (
            <img src={imgUrl} alt={auction.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Gavel className="h-12 w-12 text-slate-300" />
            </div>
          )}

          {/* Overlays */}
          <div className="absolute top-3 left-3 right-3 flex items-start justify-between">
            <AuctionStatusBadge status={auction.status} lang={lang} />
            {auction.is_featured && (
              <span className="bg-amber-400 text-amber-900 text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-1">
                <Star className="h-2.5 w-2.5" />
                {lang === 'ar' ? 'مميز' : 'Featured'}
              </span>
            )}
          </div>

          {/* Countdown */}
          {auction.status === 'live' && (
            <div className={`absolute bottom-0 left-0 right-0 px-3 py-1.5 flex items-center gap-1.5 ${
              endingSoon
                ? 'bg-red-500/90 text-white'
                : 'bg-black/60 text-white'
            }`}>
              <Clock className="h-3.5 w-3.5 shrink-0" />
              <span className="text-xs font-black font-mono">{countdown.label}</span>
              {endingSoon && <span className="text-[10px] font-bold opacity-90">{lang === 'ar' ? '— ينتهي قريباً!' : '— Ending Soon!'}</span>}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex flex-col gap-2 p-4 flex-1">
          <h3 className="font-black text-slate-800 text-sm leading-snug line-clamp-2">{auction.title}</h3>

          <div className="flex items-center gap-1.5 text-slate-500 text-xs">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{auction.location || auction.country_code}</span>
            <span className="mx-1 text-slate-300">·</span>
            <Calendar className="h-3 w-3 shrink-0" />
            <span>{auction.year}</span>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span className="bg-slate-50 px-2 py-0.5 rounded-full">{auction.brand}</span>
            <span className="bg-slate-50 px-2 py-0.5 rounded-full">{auction.car_model}</span>
          </div>

          <div className="mt-auto pt-2 border-t border-slate-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                  {lang === 'ar' ? 'المزاد الحالي' : 'Current Bid'}
                </p>
                <p className="text-lg font-black text-primary">
                  {parseFloat(auction.current_price).toLocaleString()} <span className="text-xs text-slate-400">{currencyLabel}</span>
                </p>
              </div>
              <div className={`text-right ${isRTL ? 'text-left' : 'text-right'}`}>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                  {lang === 'ar' ? 'عدد المزايدات' : 'Total Bids'}
                </p>
                <p className="text-base font-black text-slate-700 flex items-center gap-1 justify-end">
                  <TrendingUp className="h-3.5 w-3.5 text-orange-500" />
                  {auction.total_bids}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

// ── Filter Bar ────────────────────────────────────────────────────────────────

function FilterBar({
  filters, onChange, lang, isRTL,
}: {
  filters: AuctionFilters;
  onChange: (f: AuctionFilters) => void;
  lang: string;
  isRTL: boolean;
}) {
  const [open, setOpen] = useState(false);
  const activeCount = Object.values(filters).filter(
    (v) => v !== undefined && v !== '' && v !== false
  ).length;

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1 group">
          <Search className={`absolute ${isRTL ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-slate-300 h-4 w-4 transition-colors group-focus-within:text-primary`} />
          <Input
            value={filters.search || ''}
            onChange={(e) => onChange({ ...filters, search: e.target.value || undefined })}
            placeholder={lang === 'ar' ? 'ابحث عن سيارة...' : 'Search auctions...'}
            className={`h-12 rounded-2xl border-none shadow-sm bg-white ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} font-bold`}
          />
        </div>
        <Button
          variant="outline"
          className={`h-12 rounded-2xl gap-2 bg-white border-none shadow-sm font-bold ${activeCount > 0 ? 'text-primary border-primary/30' : ''}`}
          onClick={() => setOpen(!open)}
        >
          <Filter className="h-4 w-4" />
          {lang === 'ar' ? 'فلتر' : 'Filter'}
          {activeCount > 0 && (
            <span className="bg-primary text-white text-[10px] font-black w-4 h-4 rounded-full flex items-center justify-center">{activeCount}</span>
          )}
        </Button>
      </div>

      {/* Quick status pills */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: undefined, label: lang === 'ar' ? 'الكل' : 'All' },
          { key: 'live', label: lang === 'ar' ? 'نشط' : 'Live' },
          { key: 'ended', label: lang === 'ar' ? 'منتهي' : 'Ended' },
          { key: 'sold', label: lang === 'ar' ? 'مُباع' : 'Sold' },
        ].map(({ key, label }) => (
          <button
            key={String(key)}
            onClick={() => onChange({ ...filters, status: key as any })}
            className={`px-4 py-1.5 rounded-full text-xs font-black transition-all ${
              filters.status === key
                ? 'bg-primary text-white shadow-lg shadow-primary/25'
                : 'bg-white text-slate-500 shadow-sm hover:shadow-md'
            }`}
          >
            {label}
          </button>
        ))}
        <button
          onClick={() => onChange({ ...filters, ending_soon: !filters.ending_soon })}
          className={`px-4 py-1.5 rounded-full text-xs font-black transition-all ${
            filters.ending_soon
              ? 'bg-red-500 text-white shadow-lg shadow-red-200'
              : 'bg-white text-slate-500 shadow-sm hover:shadow-md'
          }`}
        >
          ⚡ {lang === 'ar' ? 'ينتهي قريباً' : 'Ending Soon'}
        </button>
      </div>

      {/* Expanded filters */}
      {open && (
        <div className="bg-white rounded-2xl shadow-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-3 animate-in fade-in slide-in-from-top-2">
          <div className="space-y-1">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider">{lang === 'ar' ? 'الماركة' : 'Brand'}</label>
            <Input
              value={filters.brand || ''}
              onChange={e => onChange({ ...filters, brand: e.target.value || undefined })}
              placeholder="Toyota, BMW..."
              className="h-9 rounded-xl text-sm font-bold border-slate-100"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider">{lang === 'ar' ? 'الدولة' : 'Country'}</label>
            <select
              value={filters.country_code || ''}
              onChange={e => onChange({ ...filters, country_code: e.target.value || undefined })}
              className="w-full h-9 rounded-xl text-sm font-bold border border-slate-100 px-3 bg-white"
            >
              <option value="">{lang === 'ar' ? 'الكل' : 'All'}</option>
              <option value="SA">{lang === 'ar' ? 'السعودية' : 'Saudi Arabia'}</option>
              <option value="AE">{lang === 'ar' ? 'الإمارات' : 'UAE'}</option>
              <option value="KW">{lang === 'ar' ? 'الكويت' : 'Kuwait'}</option>
              <option value="QA">{lang === 'ar' ? 'قطر' : 'Qatar'}</option>
              <option value="BH">{lang === 'ar' ? 'البحرين' : 'Bahrain'}</option>
              <option value="OM">{lang === 'ar' ? 'عُمان' : 'Oman'}</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider">{lang === 'ar' ? 'الحالة' : 'Condition'}</label>
            <select
              value={filters.condition || ''}
              onChange={e => onChange({ ...filters, condition: e.target.value || undefined })}
              className="w-full h-9 rounded-xl text-sm font-bold border border-slate-100 px-3 bg-white"
            >
              <option value="">{lang === 'ar' ? 'الكل' : 'All'}</option>
              <option value="new">{lang === 'ar' ? 'جديد' : 'New'}</option>
              <option value="used">{lang === 'ar' ? 'مستعمل' : 'Used'}</option>
              <option value="salvage">{lang === 'ar' ? 'متضرر' : 'Salvage'}</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider">{lang === 'ar' ? 'الترتيب' : 'Sort By'}</label>
            <select
              value={filters.ordering || '-created_at'}
              onChange={e => onChange({ ...filters, ordering: e.target.value })}
              className="w-full h-9 rounded-xl text-sm font-bold border border-slate-100 px-3 bg-white"
            >
              <option value="-created_at">{lang === 'ar' ? 'الأحدث' : 'Newest'}</option>
              <option value="end_datetime">{lang === 'ar' ? 'ينتهي قريباً' : 'Ending Soon'}</option>
              <option value="-current_price">{lang === 'ar' ? 'أعلى سعر' : 'Highest Price'}</option>
              <option value="current_price">{lang === 'ar' ? 'أدنى سعر' : 'Lowest Price'}</option>
            </select>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="col-span-2 md:col-span-4 text-slate-400 text-xs gap-1"
            onClick={() => { onChange({}); setOpen(false); }}
          >
            <X className="h-3 w-3" />
            {lang === 'ar' ? 'مسح الفلاتر' : 'Clear Filters'}
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AuctionsPage() {
  const { lang, isRTL } = useLanguage();
  const { selectedCountry, selectedCountryCode } = useCountry();
  const currencyLabel = getCurrencyLabel(selectedCountry, selectedCountryCode);
  const { toast } = useToast();
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<AuctionFilters>({ status: 'live' });
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAuctions = useCallback(async (f: AuctionFilters) => {
    try {
      const res = await auctionsApi.list(f);
      const items = Array.isArray(res) ? res : res.results;
      const count = Array.isArray(res) ? res.length : res.count;
      setAuctions(items);
      setTotal(count);
    } catch {
      toast({ variant: 'destructive', title: lang === 'ar' ? 'خطأ في تحميل المزادات' : 'Failed to load auctions' });
    } finally {
      setLoading(false);
    }
  }, [lang, toast]);

  useEffect(() => {
    setLoading(true);
    fetchAuctions(filters);
  }, [filters, fetchAuctions]);

  // Auto-refresh every 30s for live countdowns
  useEffect(() => {
    refreshIntervalRef.current = setInterval(() => fetchAuctions(filters), 30_000);
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [filters, fetchAuctions]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-primary via-primary/90 to-orange-500 text-white py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-4 bg-white/20 rounded-3xl backdrop-blur-sm">
              <Gavel className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-3xl font-black leading-tight">
                {lang === 'ar' ? 'مزادات السيارات' : 'Car Auctions'}
              </h1>
              <p className="text-white/80 text-sm font-bold">
                {lang === 'ar'
                  ? 'مزايد بأفضل سعر واربح سيارتك المثالية'
                  : 'Bid for the best price and win your dream car'}
              </p>
            </div>
          </div>

          {/* Live count pill */}
          {!loading && (
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              {total} {lang === 'ar' ? 'مزاد' : 'Auctions'}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Filter bar */}
        <FilterBar filters={filters} onChange={setFilters} lang={lang} isRTL={isRTL} />

        {/* Refresh button */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400 font-bold">
            {lang === 'ar' ? `${total} نتيجة` : `${total} results`}
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-400 gap-1 text-xs"
            onClick={() => { setLoading(true); fetchAuctions(filters); }}
          >
            <RefreshCw className="h-3 w-3" />
            {lang === 'ar' ? 'تحديث' : 'Refresh'}
          </Button>
        </div>

        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="rounded-3xl bg-white shadow-sm animate-pulse overflow-hidden">
                <div className="h-44 bg-slate-100" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-slate-100 rounded-full w-3/4" />
                  <div className="h-3 bg-slate-100 rounded-full w-1/2" />
                  <div className="h-6 bg-slate-100 rounded-full w-1/3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && auctions.length === 0 && (
          <div className="py-20 flex flex-col items-center gap-4 text-center">
            <div className="p-6 bg-slate-100 rounded-full">
              <Gavel className="h-12 w-12 text-slate-400" />
            </div>
            <h2 className="text-xl font-black text-slate-600">
              {lang === 'ar' ? 'لا توجد مزادات حالياً' : 'No auctions found'}
            </h2>
            <p className="text-slate-400 text-sm max-w-xs">
              {lang === 'ar'
                ? 'حاول تغيير الفلاتر أو تحقق مرة أخرى قريباً'
                : 'Try adjusting your filters or check back later'}
            </p>
            <Button
              variant="outline"
              className="rounded-2xl gap-2"
              onClick={() => setFilters({})}
            >
              <X className="h-4 w-4" />
              {lang === 'ar' ? 'مسح الفلاتر' : 'Clear Filters'}
            </Button>
          </div>
        )}

        {/* Auction Grid */}
        {!loading && auctions.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {auctions.map(a => (
              <AuctionCard key={a.id} auction={a} lang={lang} isRTL={isRTL} currencyLabel={currencyLabel} />
            ))}
          </div>
        )}

        {/* Auto-refresh notice */}
        {!loading && auctions.some(a => a.status === 'live') && (
          <p className="text-center text-xs text-slate-400 font-bold">
            <Sparkles className="h-3 w-3 inline mr-1" />
            {lang === 'ar' ? 'يتم التحديث تلقائياً كل 30 ثانية' : 'Auto-refreshes every 30 seconds'}
          </p>
        )}
      </div>
    </div>
  );
}
